import { LabelPayerReason } from "@redotech/congo-enums/label-payer-reason";
import { Payer } from "@redotech/congo-enums/payer";
import {
  convertAmountToDifferentCurrencyWithExchangeRate,
  Currency,
} from "@redotech/money/currencies";
import { PriceSetSchema } from "@redotech/redo-common-model-money-common/price-set";
import { StoredShippingProviderTypeSchema } from "@redotech/redo-fulfillment-carrier-common/shipment";
import { createEnum } from "@redotech/zod-util/zod-enum";
import Big from "big.js";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "../draft-return/date-schema";

export const RETURN_CARRIER_QUOTE_TTL = Temporal.Duration.from({ minutes: 30 });

export const ReturnCarrierOptionSchema = z.object({
  quoteVersion: z.literal(1),
  id: z.string(),
  carrier: z.string(),
  service: z.string(),
  quotedFee: z.string(),
  currency: createEnum(Currency),
  shippingProviderType: StoredShippingProviderTypeSchema,
  // CongoDB returns embedded dates as ISO strings on a lean read.
  quotedAt: DateFromDateOrIsoStringSchema,
  expiresAt: DateFromDateOrIsoStringSchema,
  isMerchantCarrier: z.boolean(),
});
export type ReturnCarrierOption = z.infer<typeof ReturnCarrierOptionSchema>;

export const ReturnCarrierSelectionSchema = z.object({
  coveredOption: ReturnCarrierOptionSchema,
  selectedOption: ReturnCarrierOptionSchema,
  upgradeFee: z.string(),
  /** `upgradeFee` with the presentment amount accepted at submit. */
  upgradeFeeSet: PriceSetSchema.nullish(),
});
export type ReturnCarrierSelection = z.infer<
  typeof ReturnCarrierSelectionSchema
>;

/** Only for parsed or Mongoose-hydrated options; a lean CongoDB read holds strings. */
export function optionExpiresAt(option: { expiresAt: Date }): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(option.expiresAt.getTime());
}

export function sortReturnCarrierOptionsByQuotedFee(
  options: ReturnCarrierOption[],
): ReturnCarrierOption[] {
  return [...options].sort((left, right) =>
    Big(left.quotedFee).cmp(right.quotedFee),
  );
}

export function getReturnCarrierUpgradeFee({
  coveredOption,
  selectedOption,
}: {
  coveredOption: ReturnCarrierOption;
  selectedOption: ReturnCarrierOption;
}): string {
  const difference = Big(selectedOption.quotedFee).minus(
    coveredOption.quotedFee,
  );
  return (difference.gt(0) ? difference : Big(0)).toFixed(2);
}

export function resolveReturnCarrierSelection({
  options,
  coveredOptionId,
  selectedOptionId,
  now = Temporal.Now.instant(),
}: {
  options: ReturnCarrierOption[];
  coveredOptionId: string | null | undefined;
  selectedOptionId: string | null | undefined;
  now?: Temporal.Instant;
}): ReturnCarrierSelection | undefined {
  if (!coveredOptionId || !selectedOptionId) {
    return undefined;
  }
  const coveredOption = options.find(({ id }) => id === coveredOptionId);
  const selectedOption = options.find(({ id }) => id === selectedOptionId);
  if (
    !coveredOption ||
    !selectedOption ||
    Temporal.Instant.compare(optionExpiresAt(coveredOption), now) <= 0 ||
    Temporal.Instant.compare(optionExpiresAt(selectedOption), now) <= 0
  ) {
    return undefined;
  }
  return {
    coveredOption,
    selectedOption,
    upgradeFee: getReturnCarrierUpgradeFee({ coveredOption, selectedOption }),
  };
}

export function isIncludeMultipleCarrierOptions(team: {
  settings?: { includeMultipleCarrierOptions?: boolean | null };
}): boolean {
  return team.settings?.includeMultipleCarrierOptions === true;
}

/** Draft shipment whose `fee`, `carrier` and `rateSource` describe the selected rate. */
interface CarrierSelectableShipment {
  fee: string;
  flatRateFee?: string | null;
  carrier: string;
  rateSource?: {
    isMerchantCarrier?: boolean | null;
    shippingProviderType?: string | null;
  } | null;
  carrierOptions?: unknown;
  coveredCarrierOptionId?: string | null;
  selectedCarrierUpgradeFee?: string | null;
}

function parseReturnCarrierOptions(value: unknown): ReturnCarrierOption[] {
  const parsed = ReturnCarrierOptionSchema.array().safeParse(value ?? []);
  return parsed.success ? parsed.data : [];
}

export function getCoveredReturnCarrierOption(
  shipment: Pick<
    CarrierSelectableShipment,
    "carrierOptions" | "coveredCarrierOptionId"
  >,
): ReturnCarrierOption | undefined {
  return parseReturnCarrierOptions(shipment.carrierOptions).find(
    ({ id }) => id === shipment.coveredCarrierOptionId,
  );
}

/** Options are the cheapest rate per carrier, so `carrier` identifies the selection. */
export function getSelectedReturnCarrierOption(
  shipment: Pick<CarrierSelectableShipment, "carrierOptions" | "carrier">,
): ReturnCarrierOption | undefined {
  return parseReturnCarrierOptions(shipment.carrierOptions).find(
    ({ carrier }) => carrier === shipment.carrier,
  );
}

export function applyReturnCarrierSelection(
  shipment: CarrierSelectableShipment,
  { selectedOption, upgradeFee }: ReturnCarrierSelection,
): void {
  shipment.fee = selectedOption.quotedFee;
  shipment.carrier = selectedOption.carrier;
  if (shipment.rateSource) {
    shipment.rateSource.isMerchantCarrier = selectedOption.isMerchantCarrier;
    shipment.rateSource.shippingProviderType =
      selectedOption.shippingProviderType;
  } else {
    shipment.rateSource = {
      isMerchantCarrier: selectedOption.isMerchantCarrier,
      shippingProviderType: selectedOption.shippingProviderType,
    };
  }
  shipment.selectedCarrierUpgradeFee = upgradeFee;
}

export function resetReturnCarrierSelectionToCovered(
  shipment: CarrierSelectableShipment,
): void {
  const coveredOption = getCoveredReturnCarrierOption(shipment);
  if (coveredOption) {
    applyReturnCarrierSelection(shipment, {
      coveredOption,
      selectedOption: coveredOption,
      upgradeFee: "0.00",
    });
  }
}

/**
 * Label fee to charge. When Redo covers the label the shopper is only charged
 * the covered baseline and the difference is collected as the carrier upgrade.
 * When the shopper pays for the label the selected option's price is the label
 * fee; a flat rate replaces it downstream and the upgrade is added on top.
 */
export function getChargeableShipmentFee(
  shipment: Pick<
    CarrierSelectableShipment,
    "fee" | "rateSource" | "carrierOptions" | "coveredCarrierOptionId"
  >,
  { labelPayer }: { labelPayer: Payer | null | undefined },
): { fee: string; isMerchantCarrier: boolean } {
  const selectedFee = {
    fee: shipment.fee,
    isMerchantCarrier: !!shipment.rateSource?.isMerchantCarrier,
  };
  if (labelPayer !== Payer.Redo) {
    return selectedFee;
  }
  const coveredOption = getCoveredReturnCarrierOption(shipment);
  return coveredOption
    ? {
        fee: coveredOption.quotedFee,
        isMerchantCarrier: coveredOption.isMerchantCarrier,
      }
    : selectedFee;
}

/**
 * Whether the cheapest carrier is included for the shopper: Redo covers the
 * label, or the merchant pays it for a damaged or wrong product.
 */
export function isReturnLabelCoveredForShopper({
  labelPayer,
  labelPayerReason,
}: {
  labelPayer: Payer | null | undefined;
  labelPayerReason: LabelPayerReason | null | undefined;
}): boolean {
  return (
    labelPayer === Payer.Redo ||
    (labelPayer === Payer.Merchant &&
      labelPayerReason === LabelPayerReason.MerchantDamagedOrWrongProduct)
  );
}

/**
 * Whether the shopper owes the selected-minus-covered upgrade. A covered label
 * owes it alone; a flat rate owes it on top of the flat rate.
 */
export function isReturnCarrierUpgradeOwed({
  labelPayer,
  labelPayerReason,
  isFlatRate,
}: {
  labelPayer: Payer | null | undefined;
  labelPayerReason: LabelPayerReason | null | undefined;
  isFlatRate: boolean;
}): boolean {
  return (
    isReturnLabelCoveredForShopper({ labelPayer, labelPayerReason }) ||
    (labelPayer === Payer.Customer && isFlatRate)
  );
}

/**
 * Shopper-owed upgrade for a shipment: the gap between the selected option and
 * the covered baseline.
 */
export function getShipmentCarrierUpgradeFee(
  shipment: Pick<
    CarrierSelectableShipment,
    | "carrierOptions"
    | "coveredCarrierOptionId"
    | "selectedCarrierUpgradeFee"
    | "flatRateFee"
  >,
  {
    labelPayer,
    labelPayerReason,
  }: {
    labelPayer: Payer | null | undefined;
    labelPayerReason: LabelPayerReason | null | undefined;
  },
): string {
  if (
    !isReturnCarrierUpgradeOwed({
      labelPayer,
      labelPayerReason,
      isFlatRate: !!shipment.flatRateFee,
    }) ||
    !getCoveredReturnCarrierOption(shipment)
  ) {
    return "0.00";
  }
  return Big(shipment.selectedCarrierUpgradeFee ?? 0).toFixed(2);
}

/** Quoted upgrade persisted on a submitted return, summed across its groups. */
export function sumReturnCarrierUpgradeFee(
  shipmentGroups:
    | { returnCarrierSelection?: { upgradeFee?: string | null } | null }[]
    | null
    | undefined,
): string {
  return (shipmentGroups ?? [])
    .reduce(
      (total, group) =>
        total.plus(group.returnCarrierSelection?.upgradeFee ?? 0),
      Big(0),
    )
    .toFixed(2);
}

/** Freezes what the shopper accepted at submit so later reads don't drift. */
export function snapshotReturnCarrierSelection(
  selection: ReturnCarrierSelection,
  {
    shopCurrency,
    presentmentCurrency,
    shopToPresentmentExchangeRate,
  }: {
    shopCurrency: Currency;
    presentmentCurrency: Currency;
    shopToPresentmentExchangeRate: number;
  },
): ReturnCarrierSelection {
  return {
    ...selection,
    upgradeFeeSet: {
      shopMoney: { amount: selection.upgradeFee, currencyCode: shopCurrency },
      presentmentMoney: {
        amount: convertAmountToDifferentCurrencyWithExchangeRate({
          amountInOriginalCurrency: Big(selection.upgradeFee),
          exchangeRate: Big(shopToPresentmentExchangeRate),
          toCurrency: presentmentCurrency,
        }),
        currencyCode: presentmentCurrency,
      },
    },
  };
}

/**
 * Presentment upgrade frozen at submit, summed across groups. Undefined for
 * returns submitted before it was frozen, which never collected an upgrade.
 */
export function sumReturnCarrierUpgradeFeePresentment(
  shipmentGroups:
    | {
        returnCarrierSelection?: {
          upgradeFee: string;
          upgradeFeeSet?: {
            presentmentMoney: { amount: string; currencyCode: string };
          } | null;
        } | null;
      }[]
    | null
    | undefined,
  presentmentCurrency: Currency,
): string | undefined {
  const frozenAmounts: string[] = [];
  for (const group of shipmentGroups ?? []) {
    if (!group.returnCarrierSelection) {
      continue;
    }
    const presentmentMoney =
      group.returnCarrierSelection.upgradeFeeSet?.presentmentMoney;
    if (presentmentMoney?.currencyCode !== presentmentCurrency) {
      return undefined;
    }
    frozenAmounts.push(presentmentMoney.amount);
  }
  if (frozenAmounts.length === 0) {
    return undefined;
  }
  return frozenAmounts
    .reduce((total, amount) => total.plus(amount), Big(0))
    .toString();
}

export function stripReturnCarrierOptionFields<
  T extends {
    carrierOptions?: unknown;
    coveredCarrierOptionId?: unknown;
    selectedCarrierUpgradeFee?: unknown;
  },
>(shipment: T): T {
  return {
    ...shipment,
    carrierOptions: undefined,
    coveredCarrierOptionId: undefined,
    selectedCarrierUpgradeFee: undefined,
  };
}

export function hasInvalidReturnCarrierSelection({
  shipments,
  now = Temporal.Now.instant(),
}: {
  shipments:
    | Pick<
        CarrierSelectableShipment,
        "carrier" | "carrierOptions" | "coveredCarrierOptionId"
      >[]
    | null
    | undefined;
  now?: Temporal.Instant;
}): boolean {
  return (
    shipments?.some((shipment) => {
      // A shipment that never minted a covered option has nothing to upgrade
      // from, so it can never carry an invalid selection.
      if (!shipment.coveredCarrierOptionId) {
        return false;
      }
      const selectedOption = getSelectedReturnCarrierOption(shipment);
      // The default covered selection is Redo's own rate, so an expired quote
      // must not block submit.
      if (
        !selectedOption ||
        selectedOption.id === shipment.coveredCarrierOptionId
      ) {
        return false;
      }
      return !resolveReturnCarrierSelection({
        options: parseReturnCarrierOptions(shipment.carrierOptions),
        coveredOptionId: shipment.coveredCarrierOptionId,
        selectedOptionId: selectedOption.id,
        now,
      });
    }) ?? false
  );
}
